import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { nanoid } from 'nanoid'
import { t } from '@lingui/core/macro'

import { useBrandName } from '@jotta/grpc-connect-client/config'
import { useDeleteDeviceMutation } from '@jotta/grpc-connect-client/device/useDevice'
import {
  useEmptyTrashMutation,
  useFileTrashMutation,
} from '@jotta/grpc-connect-client/file/useTrash'
import {
  removeBackgroundJobsByActionId,
  removeBackgroundJobsByType,
} from '@jotta/hooks/useBackgroundJobStore'
import type { FileActionContext } from '@jotta/types/FileActions'
import { getToastContent } from '@jotta/ui/backgroundJobToasts'
import { dismissToast, showToast } from '@jotta/ui/ToastProvider'
import { getParentPath } from '@jotta/utils/pathUtils'

import { apiPathToRoute } from '../FileRoute/utils/apiPathToRoute'
import { apiPathToRouteContext } from '../FileRoute/utils/apiPathToRouteContext'
import { useFileRouteParams } from '../FileRoute/utils/getFileRouteParams'
import { useParsedFileRouteParams } from '../FileRoute/utils/parseFileRouteParams'
import type { FolderStore } from '../Folder/FolderStore'

import { CreateFolderDialog } from './CreateFolderDialog/CreateFolderDialog'
import { DeleteDeviceDialog } from './DeleteDeviceDialog/DeleteDeviceDialog'
import { EmptyTrashDialog } from './EmptyTrashDialog/EmptyTrashDialog'
import { FileRevisionsDialog } from './FileRevisionsDialog/FileRevisionsDialog'
import { FileTransferDialog } from './FileTransferDialog/FileTransferDialog'
import { PermanentlyDeleteDialog } from './PermanentlyDeleteDialog/PermanentlyDeleteDialog'
import { ShareFileDialog } from './ShareFileDialog/ShareFileDialog'

const debug = Debug('jotta:files:FolderAction')

export interface DisplayActiveFileActionDialogProps {
  store: FolderStore
  /** Set to true when component is rendered inside a carousel or other modal view */
  isNestedModal?: boolean
}
export const DisplayActiveFileActionDialog =
  observer<DisplayActiveFileActionDialogProps>(
    function DisplayActiveFileActionDialog({ store, isNestedModal }) {
      const brandName = useBrandName()
      const navigate = useNavigate()
      const { actions } = store
      const { dispatch } = actions
      const activeFileAction = actions.activeFileActionData
      const { apiPath } = useParsedFileRouteParams()
      const fileRouteParams = useFileRouteParams()
      const [searchParams, setSearchParams] = useSearchParams()
      const actionSearchParam = searchParams.get('action')
      const shareActionContext: FileActionContext | undefined =
        actionSearchParam === 'share' && activeFileAction.type === 'Disabled'
          ? 'FOLDER_LIST'
          : undefined
      const context = store.route.context

      const deleteDeviceMutation = useDeleteDeviceMutation({
        onMutate: () => {
          actions.cancelActiveAction()
          store.selection.selectNone()
          return showToast({
            ...getToastContent('DELETE_DEVICE', 'IN_PROGRESS'),
            id: nanoid(),
          })
        },
        onSettled: (status, toastId) => {
          store.refresh()
          showToast({
            ...getToastContent('DELETE_DEVICE', status),
            id: toastId,
            onDismiss: () =>
              toastId ? removeBackgroundJobsByActionId(toastId) : undefined,
          })
        },
      })
      const emptyTrashMutation = useEmptyTrashMutation({
        onMutate: variables => {
          navigate('/web/trash', { replace: true }) // To avoid encountering "not found" error when emptying trash from within a subfolder
          actions.cancelActiveAction()
          const toastId = nanoid()

          showToast({
            ...getToastContent('EMPTY_TRASH', 'IN_PROGRESS'),
            id: toastId,
            actionButton: {
              label: t`Cancel`,
              onClick: () => {
                variables.isCancelled = true
                removeBackgroundJobsByType('DELETE_PERMANENTLY')
                dismissToast(toastId)
              },
            },
          })
          return toastId
        },
        onSettled: (status, actionId, variables) => {
          store.refresh()
          if (variables.isCancelled) {
            return
          }
          showToast({
            ...getToastContent('EMPTY_TRASH', status),
            id: actionId,
          })
        },
      })
      const { mutate: fileTrashMutation } = useFileTrashMutation({
        onMutate: variables => {
          actions.cancelActiveAction()
          store.selection.selectNone()
          const toastId = variables.toastId ?? nanoid()
          return showToast({
            ...getToastContent(variables.actionType, 'IN_PROGRESS'),
            id: toastId,
          })
        },
        onSettled: (_data, error, variables, toastId) => {
          store.refresh()
          const status = error ? 'FAILED' : 'SUCCESS'
          showToast({
            ...getToastContent(variables.actionType, status),
            id: toastId,
            onDismiss: () =>
              toastId ? removeBackgroundJobsByActionId(toastId) : undefined,
            actionButton:
              variables.actionType === 'MOVE_TO_TRASH' && status === 'SUCCESS'
                ? {
                    label: t`Undo`,
                    onClick: () => {
                      fileTrashMutation({
                        actionType: 'RESTORE',
                        paths: variables.paths,
                        toastId: toastId,
                      })
                    },
                  }
                : undefined,
          })
        },
      })

      useEffect(() => {
        if (shareActionContext) {
          dispatch('Share', shareActionContext, apiPath)
          setSearchParams(
            searchParams => {
              searchParams.delete('action')
              return searchParams
            },
            {
              replace: true,
            },
          )
        }
      }, [apiPath, dispatch, setSearchParams, shareActionContext])

      useEffect(() => {
        if (activeFileAction.type === 'Disabled') {
          return
        }
        const item = store.getItemByPath(activeFileAction.path)
        if (activeFileAction.type === 'AcceptPendingInvite') {
          actions.acceptPendingInvite(activeFileAction.path)
          return
        }
        if (activeFileAction.type === 'DeclinePendingInvite') {
          actions.declinePendingInvite(activeFileAction.path)
          return
        }
        if (!item) {
          return
        }
        const isSelection = activeFileAction.context === 'SELECTION'
        const items = isSelection ? store.selectedChildren : [item]
        const paths = items.map(item => item.path)
        switch (activeFileAction.type) {
          case 'OpenDocument': {
            const action = item.data.size === 0 ? 'editnew' : 'edit'
            const routePath = `${apiPathToRoute(activeFileAction.path, {
              context,
            })}?action=${action}`
            debug('Navigate to %s', routePath, activeFileAction.path)
            navigate(routePath)
            setTimeout(() => {
              actions.cancelActiveAction()
            }, 0)
            break
          }
          case 'Delete': {
            fileTrashMutation({
              actionType: 'MOVE_TO_TRASH',
              paths,
            })
            break
          }
          case 'Download': {
            ;(isSelection
              ? actions.downloadSelectedItems(brandName || item.name, items)
              : actions.downloadItem()
            )
              .catch(reason => {
                reportError(reason)
                showToast({
                  message: t`An error occured!`,
                  hasCloseButton: true,
                  duration: Infinity,
                })
              })
              .finally(() => actions.cancelActiveAction())
            break
          }
          case 'Restore': {
            fileTrashMutation({
              actionType: 'RESTORE',
              paths,
            })
            break
          }
          case 'NewExcelDocument':
            void actions.createDocument({
              path: activeFileAction.path,
              documentType: 'xlsx',
            })
            break
          case 'NewWordDocument':
            void actions.createDocument({
              path: activeFileAction.path,
              documentType: 'docx',
            })
            break
          case 'NewPowerpointDocument':
            void actions.createDocument({
              path: activeFileAction.path,
              documentType: 'pptx',
            })
            break
          case 'GoToFolder': {
            const enclosingFolderPath = apiPathToRoute(
              getParentPath(activeFileAction.path),
              {
                context: apiPathToRouteContext(activeFileAction.path),
                sortBy: fileRouteParams.sortBy,
                viewMode: fileRouteParams.viewMode,
              },
            ).path
            navigate(enclosingFolderPath)
            actions.cancelActiveAction()
            break
          }
          default:
            break
        }
      }, [
        activeFileAction,
        navigate,
        store,
        actions,
        context,
        fileRouteParams,
        fileTrashMutation,
        brandName,
      ])

      if (activeFileAction.type === 'Disabled') {
        return null
      }

      switch (activeFileAction.type) {
        case 'Copy':
          return (
            <FileTransferDialog
              error={activeFileAction.error}
              clearError={actions.clearActiveFileActionError}
              transferType="COPY"
              filePaths={
                activeFileAction.context === 'SELECTION'
                  ? store.selectedChildren.map(f => f.path)
                  : [activeFileAction.path]
              }
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              transferFn={actions.transferItems}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
              onFolderCreated={store.refresh}
            />
          )
        case 'CreateFolder': {
          return (
            <CreateFolderDialog
              createFolder={actions.createFolder}
              error={activeFileAction.error}
              clearError={actions.clearActiveFileActionError}
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        }
        case 'DeletePermanently': {
          return (
            <PermanentlyDeleteDialog
              paths={
                activeFileAction.context === 'SELECTION'
                  ? store.selectedChildren.map(f => f.path)
                  : [activeFileAction.path]
              }
              onConfirm={paths => {
                fileTrashMutation({
                  actionType: 'DELETE_PERMANENTLY',
                  paths,
                })
              }}
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        }
        case 'DeleteDevice': {
          const item =
            store.selectedChildren.length === 1
              ? store.selectedChildren[0]
              : store.children.find(item => item.path === activeFileAction.path)

          if (!item || !item.isDevice) {
            return
          }

          const deviceId = item.path.split('/').slice(-1)[0]

          return (
            <DeleteDeviceDialog
              onConfirm={() => {
                deleteDeviceMutation.mutate({
                  id: deviceId,
                  supportsBackgroundJobs: true,
                })
              }}
              deviceName={item.name}
              open={true}
              nested={isNestedModal}
              onClose={actions.cancelActiveAction}
              variant={'dialogs.overlay'}
            />
          )
        }
        case 'EmptyTrash':
          return (
            <EmptyTrashDialog
              onConfirm={() =>
                emptyTrashMutation.mutate({ isCancelled: false })
              }
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        case 'Move':
          return (
            <FileTransferDialog
              error={activeFileAction.error}
              clearError={actions.clearActiveFileActionError}
              transferType="MOVE"
              filePaths={
                activeFileAction.context === 'SELECTION'
                  ? store.selectedChildren.map(f => f.path)
                  : [activeFileAction.path]
              }
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              transferFn={actions.transferItems}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        case 'Revisions':
          return (
            <FileRevisionsDialog
              path={activeFileAction.path}
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        case 'Share': {
          const item = store.getItemByPath(activeFileAction.path)
          if (!item) {
            return
          }
          return (
            <ShareFileDialog
              item={item}
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
              onLeaveShare={store.actions.leaveShare}
              context={context}
            />
          )
        }
        default:
          return null
      }
    },
  )
